import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/Paginated';
import Summarization, { SummarizationOptions, SummarizationPatient } from 'models/Summarization';
import { BaseMutationParams, BaseQueryParams } from 'services/api/types';
import http from 'services/http';

export const indexSummarizations = async ({ filters, ...params }, signal: GenericAbortSignal) => {
  return http
    .get<Paginated<SummarizationOptions>>('/summarizations', {
      params: {
        ...params,
        ...serializeFilters(filters),
        'status.not': 'error',
      },
      signal,
    })
    .then((response) => ({
      ...response.data,
      data: response.data.data.map((x) => new Summarization(x)),
    }));
};

function serializeFilters(filters: Record<string, any>): Record<string, string> {
  const result: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'createdAt') {
      result[key] = `[${value[0].toISOString()},${value[1].toISOString()}]`;
    } else if (key === 'promptDisplayName') {
      // Handle array values
      result[key] = value.map((item) => item.displayName || item.toString()).join(',');
    } else {
      result[key] = value;
    }
  });

  return result;
}

type CreateParams = {
  title?: string;
  aiPromptId?: string;
  attachmentIds: string[];
  useAgenticMode?: boolean;
} & BaseMutationParams;

export async function createSummarization(params: CreateParams) {
  return http
    .post<SummarizationOptions>(`/summarizations`, params)
    .then((response) => new Summarization(response.data));
}

export const summarizationQueryKeys = {
  base: ['summarizations'],
  index: (params: Record<string, any> = {}) => ['summarizations', 'index', params],
  show: (params: Record<string, any> = {}) => ['summarizations', 'show', params],
};

export const summarizationQueryParams = {
  index: {
    include: ['attachmentCount', 'latestSummaryJob'].join(','),
  },
  show: (id: string) => ({
    id,
    include: [
      'attachments.blob',
      'attachments.extractDataAIJob',
      'patientDemographicsJob',
      'summaryJobForCurrentAttachments',
      'aiPrompt',
      'createdBy',
      'updatedBy',
    ].join(','),
  }),
};

type ShowParams = {
  id: string;
} & BaseQueryParams;

export async function showSummarization({ id, ...params }: ShowParams) {
  return http
    .get<SummarizationOptions>(`/summarizations/${id}`, { params })
    .then((response) => new Summarization(response.data));
}

type UpdateParams = {
  id: string;
  title?: string;
  patient?: SummarizationPatient;
} & BaseMutationParams;

export async function updateSummarization({ id, ...params }: UpdateParams) {
  return http
    .put<SummarizationOptions>(`/summarizations/${id}`, params)
    .then((response) => new Summarization(response.data));
}

type DeleteParams = {
  id: string;
} & BaseMutationParams;

export async function deleteSummarization({ id, ...params }: DeleteParams) {
  return http.delete(`/summarizations/${id}`, { params });
}
